import React, { useState, useCallback } from 'react';
import { ChartIcon } from './FileTree';
import SpreadsheetGantt, { TimelineView } from './SpreadsheetGantt';
import ExecutiveDashboard from './ExecutiveDashboard';
import { ShareDocModal } from './ReportingModals';
import api from '../../services/api';

// ── Document View ──────────────────────────────────────────────────────────────

const DocumentView = ({
  doc,
  docTitle,
  view,
  zoom,
  zoomLabel,
  zoomIdx,
  zoomLevels,
  treeCollapsed,
  onToggleTree,
  onTitleChange,
  onViewChange,
  onZoomIn,
  onZoomOut,
  onAddGroup,
  onAddMilestone,
  onEditRow,
}) => {
  const [shareOpen, setShareOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isGantt = view === 'gantt';
  const isTimeline = view === 'timeline';
  const isDashboard = view === 'dashboard';

  const viewOptions = [
    { key: 'gantt', label: 'Gantt', icon: 'gantt' },
    { key: 'timeline', label: 'Timeline', icon: 'timeline' },
    { key: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  ];

  const groups = doc?.groups || [];

  const handleExportPdf = useCallback(async () => {
    if (!doc?.id || exporting) return;
    setExporting(true);
    try {
      const res = await api.get(`/reporting/documents/${doc.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const ts = String(now.getFullYear()).slice(2) +
        String(now.getMonth() + 1).padStart(2, '0') +
        String(now.getDate()).padStart(2, '0') +
        String(now.getHours()).padStart(2, '0') +
        String(now.getMinutes()).padStart(2, '0');
      const safeName = (docTitle || doc.name || 'report').replace(/[^a-z0-9]/gi, '_');
      a.download = `${safeName}_${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // toast shown by interceptor
    } finally {
      setExporting(false);
    }
  }, [doc, docTitle, exporting]);

  return (
    <>
      {/* ── Document header ─────────────────────────────────────────────────── */}
      <div className="rp-doc-header rp-no-print">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="rp-doc-title-row">
            <div className="rp-doc-icon" style={{ background: 'var(--color-primary-subtle)', color: 'var(--color-brand)' }}>
              <ChartIcon type={doc?.chart_type || 'gantt'} size={16} />
            </div>
            <input
              className="rp-doc-title"
              value={docTitle}
              onChange={onTitleChange}
              placeholder="Untitled"
            />
          </div>
        </div>

        <div className="rp-doc-actions">
          <button className="rp-btn rp-btn-secondary rp-btn-sm" onClick={() => setShareOpen(true)}>
            <svg viewBox="0 0 16 16" fill="currentColor">
              <path d="M11 2.5a2.5 2.5 0 11.603 1.628l-6.97 4.02a2.5 2.5 0 010 1.704l6.97 4.02a2.5 2.5 0 11-.56.866l-6.985-4.028a2.5 2.5 0 110-3.444L10.44 3.366A2.5 2.5 0 0111 2.5z"/>
            </svg>
            Share
          </button>
          <button className="rp-btn rp-btn-secondary rp-btn-sm" onClick={handleExportPdf} disabled={exporting} title="Export as PDF">
            {exporting ? (
              <>
                <svg viewBox="0 0 16 16" fill="currentColor" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 1.5a5.5 5.5 0 110 11 5.5 5.5 0 010-11z" opacity=".3"/>
                  <path d="M8 1a7 7 0 017 7h-1.5A5.5 5.5 0 008 2.5V1z"/>
                </svg>
                Generating…
              </>
            ) : (
              <>
                <svg viewBox="0 0 16 16" fill="currentColor">
                  <path d="M14 14V4.5L9.5 0H4a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2zM9.5 3A1.5 1.5 0 0011 4.5h2V14a1 1 0 01-1 1H4a1 1 0 01-1-1V2a1 1 0 011-1h5.5v2z"/>
                  <path d="M4.603 14.087a.81.81 0 01-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 011.482-.645 19.697 19.697 0 001.062-2.227 7.269 7.269 0 01-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 01.477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 00.98 1.686 5.753 5.753 0 011.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 01-.354.416.856.856 0 01-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 01-.911-.95 11.651 11.651 0 00-1.997.406 11.307 11.307 0 01-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 01-.58.029z"/>
                </svg>
                Export PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Toolbar ─────────────────────────────────────────────────────────── */}
      <div className="rp-toolbar rp-no-print">
        <button
          className={`rp-tree-toggle-btn${treeCollapsed ? ' active' : ''}`}
          onClick={onToggleTree}
          title={treeCollapsed ? 'Show panel' : 'Hide panel'}
        >
          <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
            <path d="M0 3a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H2a2 2 0 01-2-2V3zm5-1v12h9a1 1 0 001-1V3a1 1 0 00-1-1H5zm-4 0v2h3V2H1zm0 3v2h3V5H1zm0 3v2h3V8H1z"/>
          </svg>
        </button>

        <div className="rp-toolbar-sep" />

        <div className="rp-view-switcher">
          {viewOptions.map(v => (
            <button
              key={v.key}
              className={`rp-view-btn${view === v.key ? ' active' : ''}`}
              onClick={() => onViewChange(v.key)}
            >
              <ChartIcon type={v.icon} size={13} />
              {v.label}
            </button>
          ))}
        </div>

        <div className="rp-toolbar-sep" />

        {(isGantt || isTimeline) && (
          <div className="rp-zoom-row">
            <button className="rp-zoom-btn" onClick={onZoomOut} disabled={zoomIdx === 0} title="Zoom out">
              <svg viewBox="0 0 16 16" fill="currentColor" width="11" height="11">
                <path d="M4 8a.5.5 0 01.5-.5h7a.5.5 0 010 1h-7A.5.5 0 014 8z"/>
              </svg>
            </button>
            <span className="rp-zoom-lbl">{zoomLabel}</span>
            <button className="rp-zoom-btn" onClick={onZoomIn} disabled={zoomIdx === zoomLevels.length - 1} title="Zoom in">
              <svg viewBox="0 0 16 16" fill="currentColor" width="11" height="11">
                <path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z"/>
              </svg>
            </button>
          </div>
        )}

        <div className="rp-toolbar-spacer" />

        {!isDashboard && (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button className="rp-btn rp-btn-secondary rp-btn-sm" onClick={onAddMilestone} title="Add a global milestone">
              <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                <path d="M8 1l2.2 5.4H16l-4.6 3.4 1.7 5.4L8 11.6l-5.1 3.6 1.7-5.4L0 6.4h5.8L8 1z"/>
              </svg>
              Add Milestone
            </button>
            <button className="rp-btn rp-btn-primary rp-btn-sm" onClick={onAddGroup}>
              <svg viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3v3a.5.5 0 01-1 0v-3h-3a.5.5 0 010-1h3v-3A.5.5 0 018 4z"/>
              </svg>
              Add Group
            </button>
          </div>
        )}
      </div>

      {/* ── Active view ─────────────────────────────────────────────────────── */}
      {isGantt && <SpreadsheetGantt doc={doc} groups={groups} zoom={zoom} onEditRow={onEditRow} />}
      {isTimeline && <TimelineView groups={groups} zoom={zoom} />}
      {isDashboard && <ExecutiveDashboard doc={doc} groups={groups} />}

      {/* ── Share modal ─────────────────────────────────────────────────────── */}
      <ShareDocModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        docId={doc?.id}
        docName={docTitle || doc?.name}
      />
    </>
  );
};

export default DocumentView;
